import { ResultAsync } from "neverthrow";

import { buildInternalError } from "@thalamos/common";
import type { UserUnbranded } from "@thalamos/vision-common";

import api from "../index.js";

/**
 * Factory that creates a user search function scoped to an organisation.
 *
 * Captures the optional `commandUnitId` in the closure so that the returned
 * function only requires the query, limit, and offset — keeping consumers
 * decoupled from the API layer.
 */
export const createUserSearch = (commandUnitId?: string) => {
  return (
    query: string,
    limit: number,
    offset: number,
  ): ResultAsync<UserUnbranded[], ReturnType<typeof buildInternalError>> =>
    ResultAsync.fromPromise(
      api.user.search(query, limit, offset, commandUnitId).then((result) => {
        if (result.status === 200) {
          return result.data.results;
        }
        return [];
      }),
      (error) =>
        buildInternalError({
          message: "Failed to search users",
          issues: [error instanceof Error ? error.message : String(error)],
        }),
    );
};

/**
 * The type of the search function returned by {@link createUserSearch}.
 * Use this as the prop type so it stays in sync with the API layer automatically.
 */
export type SearchUsersFn = ReturnType<typeof createUserSearch>;
